import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Observable, catchError, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export type CookieConsentDecision = 'pending' | 'accepted' | 'declined';

export const COOKIE_CONSENT_NAME = 'hebern_cookie_consent';
export const COOKIE_CONSENT_VALUE = 'accepted';
const COOKIE_CONSENT_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
/** sessionStorage only — decline must not create a cookie; clears when the tab/session ends. */
const COOKIE_DECISION_STORAGE_KEY = 'hebern.cookie.consent.decision';

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly decisionSignal = signal<CookieConsentDecision>('pending');

  readonly decision = this.decisionSignal.asReadonly();
  readonly hasConsent = computed(() => this.decisionSignal() === 'accepted');
  readonly needsPrompt = computed(() => this.decisionSignal() === 'pending');

  constructor() {
    if (!this.isBrowser()) {
      return;
    }

    if (this.readConsentCookie()) {
      this.applyDecision('accepted');
      this.syncApiConsent();
      return;
    }

    const stored = this.readDecision();
    if (stored === 'declined' || stored === 'accepted') {
      this.decisionSignal.set(stored);
      if (stored === 'accepted') {
        this.syncApiConsent();
      }
    }
  }

  accept(): Observable<unknown> {
    if (!this.isBrowser()) {
      return of(null);
    }

    // Dismiss immediately; API/cookie sync must not block the prompt.
    this.applyDecision('accepted');
    this.writeConsentCookie();

    return this.http
      .post(
        `${environment.encryptionApiUrl}/api/v1/cookie-consent`,
        {},
        { withCredentials: true }
      )
      .pipe(
        tap(() => this.writeConsentCookie()),
        catchError(() => of(null))
      );
  }

  decline() {
    if (!this.isBrowser()) {
      return;
    }

    // No cookies on decline — only remember the choice for this browser session.
    this.applyDecision('declined');
  }

  private syncApiConsent() {
    this.http
      .post(
        `${environment.encryptionApiUrl}/api/v1/cookie-consent`,
        {},
        { withCredentials: true }
      )
      .subscribe({ error: () => undefined });
  }

  private applyDecision(decision: Exclude<CookieConsentDecision, 'pending'>) {
    this.writeDecision(decision);
    this.decisionSignal.set(decision);
  }

  private readDecision(): CookieConsentDecision | null {
    try {
      const value = sessionStorage.getItem(COOKIE_DECISION_STORAGE_KEY);
      if (value === 'accepted' || value === 'declined') {
        return value;
      }
    } catch {
      // Ignore storage failures.
    }
    return null;
  }

  private writeDecision(decision: Exclude<CookieConsentDecision, 'pending'>) {
    try {
      sessionStorage.setItem(COOKIE_DECISION_STORAGE_KEY, decision);
    } catch {
      // Ignore storage failures.
    }
  }

  private readConsentCookie(): boolean {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    for (const part of cookies) {
      const [rawName, ...rest] = part.trim().split('=');
      if (rawName !== COOKIE_CONSENT_NAME) {
        continue;
      }
      const value = decodeURIComponent(rest.join('=')).trim().toLowerCase();
      return (
        value === COOKIE_CONSENT_VALUE || value === 'true' || value === '1'
      );
    }
    return false;
  }

  private writeConsentCookie() {
    const parts = [
      `${COOKIE_CONSENT_NAME}=${COOKIE_CONSENT_VALUE}`,
      'Path=/',
      `Max-Age=${COOKIE_CONSENT_MAX_AGE_SECONDS}`,
      'SameSite=Lax',
    ];
    if (window.isSecureContext) {
      parts.push('Secure');
    }
    document.cookie = parts.join('; ');
  }

  private isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
